/**
 * Shared pin store for the agent-transparency surfaces (Crucible
 * #687, design `docs/c3-agents-panel-design.md` §3.1).
 *
 * The pin is a 10s ephemeral override the user applies by clicking on
 * an agent row (panel) or the banner body. Both surfaces consume the
 * same pin so they never disagree about which row is the spotlight.
 * Lifting the pin into a module-scoped store avoids a `<PinProvider>`
 * wrapping the centre-column shell and keeps the C2 banner's pin
 * surface area identical (it merely swaps `useState` for the hooks
 * here in C3's B2 migration).
 *
 *   - `usePin(gameId)` returns the live pin for the game, or `null`.
 *     Re-renders on every replace / clear via `useSyncExternalStore`.
 *   - `useSetPin(gameId)` returns a stable setter callback. Passing
 *     `null` clears immediately. Passing the same `agentId` that's
 *     already pinned for this game clears as well (mirrors C2's
 *     click-while-pinned release).
 *   - `clearPin(gameId)` is the non-hook teardown for consumer
 *     `useEffect` cleanup. Required because a module-scoped store
 *     is NOT cleared by a React `key={gameId}` remount on its own
 *     (Codex round-1 MED on the design doc).
 *
 * Pin shape mirrors C2 (`ActivityBannerPin` after Crucible #703):
 * `{sessionId, agentId, expiresAt}`. The `sessionId` lets the
 * downstream `resolveSelectedRow` filter out cross-session pins that
 * would otherwise resurrect against a new session's `__main__` row.
 *
 * The hooks here do NOT gate on `useFeatureFlags`. They are pure
 * primitives; gating happens at the consumer (banner / panel). Calling
 * `usePin` outside of a flag-gated parent is safe (returns `null` when
 * the store is empty) but surfacing the pin to UI without the flag
 * gate is the consumer's responsibility. B2's banner migration reuses
 * the existing `!flags.loaded || !flags.agentTransparency` early-
 * return to keep the gate centralised.
 *
 * British spelling throughout (behaviour, centre, colour, etc.).
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * The pin payload — scoped to a specific `(sessionId, agentId)` so a
 * pin from session A never resurrects when session B happens to use
 * the same reserved id (`__main__`) on its first push. The expiry is
 * the absolute epoch ms; the one-shot timer compares against
 * `Date.now()` at fire time.
 */
export interface Pin {
  sessionId: string;
  agentId: string;
  expiresAt: number;
}

/**
 * Pin lifetime in ms (design §3.1; matches C2's `PIN_DURATION_MS`).
 * Exported so consumers + tests stay single-sourced on the constant.
 */
export const PIN_DURATION_MS = 10_000;

// ---------------------------------------------------------------------------
// Module-scoped state — a `Map<gameId, Pin>` with per-`gameId`
// subscriber sets and one-shot expiry timers.
// ---------------------------------------------------------------------------

const pins = new Map<string, Pin>();
const subscribers = new Map<string, Set<() => void>>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function notify(gameId: string): void {
  const subs = subscribers.get(gameId);
  if (subs === undefined) return;
  // Snapshot the subscriber set before iterating; a subscriber
  // callback may unsubscribe itself mid-notify (React 18+ strict-
  // mode mount/unmount cycles do this), which would mutate the
  // underlying Set during iteration.
  for (const cb of Array.from(subs)) cb();
}

function cancelTimer(gameId: string): void {
  const id = timers.get(gameId);
  if (id !== undefined) {
    clearTimeout(id);
    timers.delete(gameId);
  }
}

/**
 * Clear the pin for `gameId`. Cancels any in-flight expiry timer,
 * removes the entry from the store, and notifies subscribers so live
 * `usePin(gameId)` callers re-render with `null`.
 *
 * Consumers MUST call this in a `useEffect` cleanup keyed on
 * `gameId` so a Workspace unmount or game switch drains the
 * module-scoped state. A React `key={gameId}` reset on the parent
 * does NOT clear module-scoped state on its own — see design §3.1
 * for the Codex round-1 MED note.
 *
 *     useEffect(() => {
 *         return () => { clearPin(gameId); };
 *     }, [gameId]);
 */
export function clearPin(gameId: string): void {
  cancelTimer(gameId);
  const had = pins.delete(gameId);
  if (had) notify(gameId);
}

/**
 * Internal: subscribe to pin changes for `gameId`. Returns an
 * unsubscribe function. Used by `usePin` via `useSyncExternalStore`.
 */
function subscribe(gameId: string, cb: () => void): () => void {
  let set = subscribers.get(gameId);
  if (set === undefined) {
    set = new Set();
    subscribers.set(gameId, set);
  }
  set.add(cb);
  return () => {
    const s = subscribers.get(gameId);
    if (s === undefined) return;
    s.delete(cb);
    if (s.size === 0) subscribers.delete(gameId);
  };
}

/**
 * Internal: read the current pin for `gameId`, or `null`. Stable
 * reference for an unchanged value so `useSyncExternalStore` doesn't
 * tear or trigger spurious re-renders.
 */
function getSnapshot(gameId: string): Pin | null {
  return pins.get(gameId) ?? null;
}

/**
 * React hook — returns the live pin for `gameId`, or `null`. Re-
 * renders the consumer on every replace / clear. The hook itself
 * does NOT gate on the feature flag (see file-level note); call it
 * only inside a flag-gated parent if the value drives UI.
 *
 * Identity guarantee: the same `Pin` reference is returned on every
 * render until the pin is replaced or cleared. Setting a new pin
 * (even with the same `agentId`) returns a fresh reference; this
 * matches the C2 banner's `useState<Pin | null>` reference semantics.
 */
export function usePin(gameId: string): Pin | null {
  return useSyncExternalStore(
    useCallback((cb) => subscribe(gameId, cb), [gameId]),
    useCallback(() => getSnapshot(gameId), [gameId]),
    // Server snapshot — Crucible is renderer-only, but keeping
    // the third argument matches the canonical
    // `useSyncExternalStore` shape. Returns `null` because there
    // is never a server-rendered pin.
    () => null,
  );
}

/**
 * Internal mutator — used by `useSetPin`'s callback AND a future B2
 * surface that wants to set a pin imperatively (e.g. a keyboard
 * shortcut). Kept private for now; B2 / B-follow-up can export it
 * once a second consumer is real.
 *
 * Behaviour:
 *   - `pin === null` → equivalent to `clearPin(gameId)`.
 *   - else → replace the pin and schedule a one-shot timer to clear
 *     it at `pin.expiresAt - Date.now()` ms from now. Any in-flight
 *     timer is cancelled first.
 *
 * Returns `void` — consumers read via `usePin` after this returns.
 */
function setPinInternal(gameId: string, pin: Pin | null): void {
  if (pin === null) {
    clearPin(gameId);
    return;
  }
  cancelTimer(gameId);
  pins.set(gameId, pin);
  const remaining = pin.expiresAt - Date.now();
  if (remaining <= 0) {
    // Constructed with an already-expired timestamp — clear on
    // the next tick so `usePin` callers see the pin briefly,
    // then `null`. Mirrors the C2 banner's `remaining <= 0`
    // branch which clears immediately.
    pins.delete(gameId);
    notify(gameId);
    return;
  }
  const id = setTimeout(() => {
    // Only clear if the pin we set is still the active one (a
    // second `setPinInternal` may have replaced it; that call
    // cancelled this timer, so this branch shouldn't fire — but
    // guard anyway in case a manual `clearPin` raced).
    const live = pins.get(gameId);
    if (live === pin) {
      pins.delete(gameId);
      timers.delete(gameId);
      notify(gameId);
    }
  }, remaining);
  timers.set(gameId, id);
  notify(gameId);
}

/**
 * React hook — returns a stable setter callback for `gameId`.
 *
 * Consumer-facing surface matches the design doc §3.1 contract:
 * `useSetPin(gameId, sessionId)(agentId)`. The `sessionId` argument
 * is captured per-render via a ref so the callback identity stays
 * stable across renders even as `sessionId` updates (memoised
 * children won't re-render on a session roll just because the
 * setter reference changed). Passing the current `sessionId` on every
 * render is the consumer's responsibility — typical usage is to read
 * it off the latest `ActivitySnapshot`.
 *
 * Calling shapes (consumer-facing):
 *   - `setPin(agentId)` → sets a 10s pin on `(sessionId, agentId)`.
 *     If a pin with the SAME `(sessionId, agentId)` is already
 *     active for this game, the call clears instead (toggle-off,
 *     matches C2's click-while-pinned release).
 *   - `setPin(null)` → clears the pin for this game.
 *
 * A pin set during one session's `sessionId` does NOT toggle off
 * when the same `agentId` is set during a later session — the store
 * treats that as a fresh pin (session bleed-through guard).
 *
 * The setter reads the live pin via `pins.get(gameId)` so it always
 * sees the most-recent state, not a stale closure.
 *
 * Flag-gating: the setter is a no-op only if the consumer's parent
 * is flag-gated (i.e. unreachable). The hook itself does not gate.
 */
export function useSetPin(
  gameId: string,
  sessionId: string | null,
): (agentId: string | null) => void {
  // Capture `sessionId` in a ref so the setter callback identity
  // stays stable across re-renders (memoised children won't churn
  // on a session roll). The ref is written on the synchronous
  // render path so a click fired in the same tick as the rerender
  // sees the latest value; a post-commit `useEffect` re-asserts
  // the write so a concurrent-render abort settles to the
  // committed value.
  const sessionIdRef = useRef<string | null>(sessionId);
  sessionIdRef.current = sessionId;
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  return useCallback(
    (agentId) => {
      if (agentId === null) {
        clearPin(gameId);
        return;
      }
      const session = sessionIdRef.current;
      if (session === null) {
        // No session yet (renderer pre-snapshot) — silently
        // drop. The consumer's parent shouldn't be calling
        // the setter before the first push, but a stray
        // click during a transient state shouldn't crash.
        return;
      }
      const live = pins.get(gameId);
      // Toggle-off: clicking the same `(sessionId, agentId)`
      // twice within the 10s window releases the pin (C2 banner
      // click-while-pinned semantics). A different `sessionId`
      // does NOT toggle off — that's a session bleed-through
      // which the store deliberately treats as a fresh pin.
      if (live !== undefined && live.agentId === agentId && live.sessionId === session) {
        clearPin(gameId);
        return;
      }
      setPinInternal(gameId, {
        sessionId: session,
        agentId,
        expiresAt: Date.now() + PIN_DURATION_MS,
      });
    },
    [gameId],
  );
}

/**
 * Test-only escape hatch — set the pin with an explicit `expiresAt`.
 * Production code uses `useSetPin` which computes a fresh 10s
 * window; tests use this when they need to exercise the
 * already-expired branch or a custom expiry.
 *
 * Not exported via any public barrel; kept on the module surface
 * only so the test file can import it.
 */
export function __setPinForTests(gameId: string, pin: Pin | null): void {
  setPinInternal(gameId, pin);
}

// ---------------------------------------------------------------------------
// Test-only helpers
// ---------------------------------------------------------------------------

/**
 * Reset all module state. Test-only — not exported from any public
 * barrel. Required because the store is module-scoped (a `Map`
 * defined at module load), so `vi.resetModules()` would otherwise be
 * the only way to drain it between tests.
 */
export function __resetForTests(): void {
  for (const gameId of Array.from(timers.keys())) {
    cancelTimer(gameId);
  }
  pins.clear();
  subscribers.clear();
  timers.clear();
}
